import React from "react";
import { Navigate } from "react-router-dom";
import {
  useHasPermission,
  useHasAnyPermission,
} from "../../../../common/permissions";

interface SettingsRouteGuardProps {
  children: React.ReactElement;
  routeType:
    | "account"
    | "providers-and-patients"
    | "charge-and-taxes"
    | "roles-and-permissions"
    | "notifications"
    | "global-holidays"
    | "profile";
}

const SettingsRouteGuard: React.FC<SettingsRouteGuardProps> = ({
  children,
  routeType,
}) => {
  // Check permissions based on route type
  const hasAccountPermission = useHasAnyPermission(
    ["Admin Account User", "Admin Account Payment source"],
    "viewAll",
  );

  const hasProvidersAndLocationsPermission = useHasAnyPermission(
    ["Admin Providers", "Admin Locations", "Admin Location Groups"],
    "viewAll",
  );

  const hasChargeAndTaxesPermission = useHasPermission(
    "Admin Charge and Taxes",
    "viewAll",
  );
  const hasRolesAndPermissionsPermission = useHasPermission(
    "Admin Roles and Permissions",
    "viewAll",
  );
  const hasNotificationsPermission = useHasPermission(
    "Admin Notifications",
    "viewAll",
  );
  const hasGlobalHolidaysPermission = useHasPermission(
    "Admin Settings",
    "viewAll",
  );

  // Determine if user has access to this route
  let hasAccess = false;

  switch (routeType) {
    case "account":
      hasAccess = hasAccountPermission;
      break;
    case "providers-and-patients":
      hasAccess = hasProvidersAndLocationsPermission;
      break;
    case "charge-and-taxes":
      hasAccess = hasChargeAndTaxesPermission;
      break;
    case "roles-and-permissions":
      hasAccess = hasRolesAndPermissionsPermission;
      break;
    case "notifications":
      hasAccess = hasNotificationsPermission;
      break;
    case "global-holidays":
      hasAccess = hasGlobalHolidaysPermission;
      break;
    case "profile":
      hasAccess = true; // Profile is always accessible
      break;
    default:
      hasAccess = false;
  }

  if (!hasAccess) {
    // Find the first accessible tab and redirect there
    if (hasAccountPermission) {
      return <Navigate to="/admin/settings/account" replace />;
    } else if (hasProvidersAndLocationsPermission) {
      return <Navigate to="/admin/settings/providers-and-patients" replace />;
    } else if (hasChargeAndTaxesPermission) {
      return <Navigate to="/admin/settings/charge-and-taxes" replace />;
    } else if (hasRolesAndPermissionsPermission) {
      return <Navigate to="/admin/settings/roles-and-permissions" replace />;
    } else if (hasNotificationsPermission) {
      return <Navigate to="/admin/settings/notifications" replace />;
    } else {
      // If no settings permissions, redirect to profile (always accessible)
      return <Navigate to="/admin/settings/profile" replace />;
    }
  }

  return children;
};

export default SettingsRouteGuard;
